import React from 'react';
import { Box, Button, Typography, Divider, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface ChangeRow {
  key: string;
  label: string;
  before: string;
  after: string;
}

interface Props {
  before: Record<string, any>;
  changes: ChangeRow[];
  state: 'pending' | 'approved' | 'rejected';
  onResolve?: (approved: boolean) => void;
}

const InfoRow = ({ label, value }: { label: string; value: any }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.3 }}>
      <Typography variant="caption" sx={{ minWidth: 96, color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {String(value)}
      </Typography>
    </Box>
  );
};

const isCancelChange = (changes: ChangeRow[]): boolean =>
  changes.some((c) => c.key === 'status' && /キャンセル|cancelled/i.test(c.after));

export default function AgentUpdateConfirmCard({ before, changes, state, onResolve }: Props) {
  const isCancel = isCancelChange(changes);
  const headerBg = isCancel
    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
    : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
  const headerLabel = isCancel ? '⚠️ 予約のキャンセル確認' : '✏️ 予約更新の確認';
  const borderColor = isCancel ? '#dc2626' : '#2563eb';
  const bgColor = isCancel ? '#fef2f2' : '#eff6ff';
  const accentColor = isCancel ? '#991b1b' : '#1e3a8a';

  return (
    <Box sx={{ mx: 1.5, my: 0.8 }}>
      <Box
        sx={{
          border: `2px solid ${borderColor}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: bgColor,
        }}
      >
        <Box sx={{ px: 1.5, py: 0.8, background: headerBg, color: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {headerLabel}
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          {/* 対象予約の概要 */}
          <Typography variant="caption" sx={{ color: accentColor, fontWeight: 700 }}>
            対象予約
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <InfoRow label="予約番号" value={before.contact_code} />
            <InfoRow label="ID" value={before.reservation_id} />
            <InfoRow label="予約者名" value={before.customer_name} />
            <InfoRow label="車両" value={before.car_type} />
            <InfoRow label="貸出日時" value={before.pickup_datetime} />
            <InfoRow label="返却日時" value={before.return_datetime} />
            <InfoRow label="現ステータス" value={before.status} />
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* 変更内容 */}
          <Typography variant="caption" sx={{ color: accentColor, fontWeight: 700 }}>
            変更内容（{changes.length} 項目）
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {changes.map((c) => (
              <Box
                key={c.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 0.8,
                  my: 0.3,
                  borderRadius: 1,
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Typography variant="caption" sx={{ minWidth: 96, color: 'text.secondary', fontWeight: 700 }}>
                  {c.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                  {c.before || '（未設定）'}
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 14, color: accentColor }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: accentColor }}>
                  {c.after}
                </Typography>
              </Box>
            ))}
          </Box>

          {state === 'pending' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.2 }}>
              <Button
                size="small"
                variant="contained"
                color={isCancel ? 'error' : 'primary'}
                startIcon={<CheckCircleIcon />}
                onClick={() => onResolve?.(true)}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {isCancel ? 'キャンセルを確定する' : 'この内容で更新する'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                onClick={() => onResolve?.(false)}
                sx={{ textTransform: 'none' }}
              >
                やめる
              </Button>
            </Box>
          )}
          {state === 'approved' && (
            <Chip icon={<CheckCircleIcon />} label="承認済み" color="success" size="small" sx={{ mt: 1 }} />
          )}
          {state === 'rejected' && (
            <Chip icon={<CancelIcon />} label="キャンセル" color="default" size="small" sx={{ mt: 1 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
